import { RECURRENCE_UNIT_SHORT } from '../../../utils/ruleRecurrence';
import { countRu, EVENT_FORMS } from '../../../utils/pluralRu';

/*
 * ТЗ №2 §6: enum → пользовательский label. Frontend НЕ вычисляет
 * lifecycle/severity/trend — только подписывает то, что пришло.
 */

export const STATUS_LABELS = {
  active: 'Активная',
  improving: 'Улучшается',
  resolved: 'Решена',
  reopened: 'Возобновилась',
};

export const SEVERITY_LABELS = {
  critical: 'Критично',
  attention: 'Требует внимания',
};

export const TREND_LABELS = {
  new: 'Новая',
  stable: 'Стабильно',
  improving: 'Снижается',
  worsening: 'Ухудшается',
};

/*
 * FE-CORR-07 FE-005: пользователь видит одно состояние проблемы вместо пары
 * «статус» + «динамика». Оси status/trend в DTO не меняются, здесь только
 * выбирается подпись по приоритету ТЗ. Неизвестное значение — нейтральное
 * «Состояние неизвестно», а не «Решена» по умолчанию.
 */
export const DISPLAY_STATUS_LABELS = {
  new: 'Новая',
  worsening: 'Ухудшается',
  improving: 'Улучшается',
  stable: 'Стабильно',
  resolved: 'Решена',
  unknown: 'Состояние неизвестно',
};

const KNOWN_STATUSES = ['active', 'improving', 'resolved', 'reopened'];
const KNOWN_TRENDS = ['new', 'stable', 'improving', 'worsening'];

export const deriveIssueDisplayStatus = (status, trend) => {
  let id = 'unknown';
  if (!KNOWN_STATUSES.includes(status)) id = 'unknown';
  else if (status === 'resolved') id = 'resolved';
  else if (trend === 'worsening') id = 'worsening';
  else if (trend === 'improving' || status === 'improving') id = 'improving';
  else if (trend === 'new') id = 'new';
  else if (KNOWN_TRENDS.includes(trend)) id = 'stable';
  return { id, label: DISPLAY_STATUS_LABELS[id] };
};

/** ТЗ №2 §7: lifecycle tabs; counts — из status_counts. */
export const STATUS_TABS = [
  { id: '', label: 'Все' },
  { id: 'active', label: 'Активные' },
  { id: 'improving', label: 'Улучшается' },
  { id: 'resolved', label: 'Решённые' },
  { id: 'reopened', label: 'Возобновившиеся' },
];

const BLOCKED_REASONS = {
  no_control_data: 'Недостаточно данных наблюдения для подтверждения решения.',
  connection_state_missing: 'Нет надёжных данных о состоянии камеры.',
  camera_stopped: 'Камера была остановлена — отсутствие нарушений нельзя считать подтверждением решения.',
  camera_offline: 'Камера офлайн — отсутствие нарушений нельзя считать подтверждением решения.',
  insufficient_control: 'Недостаточная непрерывность контроля для подтверждения решения.',
};
const BLOCKED_FALLBACK = 'Недостаточно данных контроля для подтверждения решения.';

/** ТЗ №2 §8.1: raw код будущих причин пользователю не показывать. */
export const describeBlockedReason = (code) => {
  if (!code) return null;
  if (BLOCKED_REASONS[code]) return { text: BLOCKED_REASONS[code], known: true };
  return { text: BLOCKED_FALLBACK, known: false };
};

/** «1 день», «3 дня», «7 дней», «21 день». */
export const pluralDays = (n) => {
  const abs = Math.abs(Number(n)) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return `${n} дней`;
  if (last === 1) return `${n} день`;
  if (last >= 2 && last <= 4) return `${n} дня`;
  return `${n} дней`;
};

/** Творительный: «с предыдущими 7 днями», «1 днём». */
export const pluralDaysInstrumental = (n) => {
  const abs = Math.abs(Number(n)) % 100;
  const last = abs % 10;
  if (!(abs > 10 && abs < 20) && last === 1) return `${n} днём`;
  return `${n} днями`;
};

/** ТЗ №2 §1: N — только из фактически применённого list.window_days. */
export const buildPeriodLabel = (n) =>
  `Последние ${pluralDays(n)} в сравнении с предыдущими ${pluralDaysInstrumental(n)}`;

export const countTabTotal = (statusCounts) =>
  Object.values(statusCounts || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);

/*
 * OPS-ISSUES04 (ТЗ v4 §30, FE-FIX27) и recurrence episodes (§35, FE-FIX32).
 *
 * tracking_state, archive_state и lifecycle status — три независимые оси.
 * «Больше не отслеживается» не значит «решена» и не значит «в архиве», поэтому
 * отдельный badge добавляется к lifecycle badge, а не заменяет его.
 */

export const TRACKING_REASON_LABELS = {
  rule_disabled: 'Правило выключено',
  rule_deleted: 'Правило удалено',
  analytics_excluded: 'События исключены из аналитики',
  rule_scope_changed: 'Изменилась область действия правила',
};

const TRACKING_FALLBACK = 'Отслеживание остановлено';

/** Неизвестную причину показываем нейтрально: raw-код пользователю не нужен. */
export const describeTrackingReason = (code) => {
  if (!code) return TRACKING_FALLBACK;
  return TRACKING_REASON_LABELS[code] || TRACKING_FALLBACK;
};

const NOT_TRACKED_PREFIX = 'Больше не отслеживается';

/**
 * FE-CORR-07 FE-006: причина для единственного блока «Больше не
 * отслеживается». Если explanation backend уже начинается этими словами,
 * берётся только его первое предложение («…: правило отключено»): хвост
 * пересказывает устройство расчёта техническими словами. Иначе — подпись по
 * tracking_reason. consumesExplanation — explanation целиком ушёл в этот блок
 * и отдельным абзацем не повторяется.
 */
export const describeNotTracked = (explanation, reasonCode) => {
  const text = typeof explanation === 'string' ? explanation.trim() : '';
  if (text.startsWith(NOT_TRACKED_PREFIX)) {
    const rest = text.slice(NOT_TRACKED_PREFIX.length).replace(/^[\s:.,—–-]+/, '');
    const sentence = rest.split(/(?<=[.!?])\s/)[0].replace(/[.!?]+$/, '').trim();
    if (sentence) {
      return {
        reason: sentence.charAt(0).toUpperCase() + sentence.slice(1),
        consumesExplanation: true,
      };
    }
    return { reason: describeTrackingReason(reasonCode), consumesExplanation: true };
  }
  return { reason: describeTrackingReason(reasonCode), consumesExplanation: false };
};

/** ТЗ v4 §35: occurrence_state — строка в detail, не бейдж (FE-CORR-07 FE-005). */
export const OCCURRENCE_LABELS = {
  new: 'Появилась впервые',
  regular: 'Повторяется регулярно',
  reopened: 'Возобновилась',
};

/** archive_state как отдельный вид списка; deleted — tombstone, в списках его нет. */
export const ARCHIVE_VIEWS = [
  // «Текущие», а не «Активные»: lifecycle status active — это другая ось,
  // и две кнопки с одинаковой подписью читались бы как один фильтр.
  { id: 'active', label: 'Текущие' },
  { id: 'archived', label: 'Архив' },
];

export const ARCHIVE_STATE_LABELS = {
  active: 'Активная',
  archived: 'В архиве',
  deleted: 'Удалена',
};

/**
 * Действия доступны по виду списка: архивировать можно только активную,
 * восстановить и удалить — только из архива (delete разрешён лишь оттуда).
 */
export const BULK_ACTIONS = {
  archive: {
    id: 'archive',
    label: 'Архивировать',
    view: 'active',
    confirm: (count) => `Архивировать выбранные проблемы (${count})?\n`
      + 'Связанные события и доказательства не затрагиваются.',
  },
  restore: {
    id: 'restore',
    label: 'Восстановить',
    view: 'archived',
    confirm: (count) => `Восстановить выбранные проблемы (${count})? Они вернутся в активный список.`,
  },
  delete: {
    id: 'delete',
    label: 'Удалить',
    view: 'archived',
    destructive: true,
    confirm: (count) => `Удалить выбранные проблемы (${count})?\n`
      + 'Проблема останется как удалённая запись и перестанет появляться в списках. '
      + 'Связанные события и доказательства не удаляются.',
  },
};

export const bulkActionsForView = (view) =>
  Object.values(BULK_ACTIONS).filter((action) => action.view === view);

/** Счётчики bulk-ответа; «уже в этом состоянии» — нормальная идемпотентность. */
export const describeBulkActionResult = (result) =>
  `Изменено ${result?.changed_issue_count ?? 0} из ${result?.affected_issue_count ?? 0}`
  + ` · ${result?.already_issue_count ?? 0} уже в этом состоянии`;

/**
 * Публичные recurrence-поля criteria (ТЗ v4 §33/§35). Внутреннее имя
 * recurrence_count наружу не используется и здесь не читается.
 */
export const describeRecurrenceCriteria = (criteria) => {
  const threshold = criteria?.recurrence_threshold_count;
  if (threshold == null) return null;
  const value = criteria?.recurrence_window_value;
  const unit = criteria?.recurrence_window_unit;
  const base = `Порог повторяемости — ${countRu(threshold, EVENT_FORMS)}`;
  if (value == null || !unit) return base;
  return `${base} за ${value} ${RECURRENCE_UNIT_SHORT[unit] || unit}`;
};

/*
 * ТЗ V5 №15: словарь пояснений плашек — поле + код → подпись и описание.
 * Тексты согласованы в ТЗ; frontend ничего не пересчитывает, а только
 * объясняет пришедшее значение. Одно и то же слово в разных полях («Новая»
 * в trend и в occurrence_state, «Улучшается» в trend и в status) объясняется
 * по-разному.
 */
export const ISSUE_FIELD_CONTEXT = {
  severity: 'Важность',
  trend: 'Динамика',
  status: 'Состояние проблемы',
  occurrence_state: 'Эпизод',
  tracking_state: 'Отслеживание',
};

export const ISSUE_TERM_HELP = {
  severity: {
    critical: {
      label: 'Критично',
      description: 'За текущий период число повторений проблемы достигло порога повышенной важности, '
        + 'рассчитанного по настройкам правила. Это приоритет для проверки, а не сообщение об аварии оборудования.',
    },
    attention: {
      label: 'Требует внимания',
      description: 'Проблема зафиксирована и требует проверки, но сейчас не достигла порога «Критично». '
        + 'Это не означает, что проблему можно игнорировать.',
    },
  },
  trend: {
    new: {
      label: 'Новая',
      description: 'В предыдущем сравнимом периоде проблема не регистрировалась, а в текущем появилась. '
        + 'Это оценка динамики, не обязательно первая запись за всё время.',
    },
    stable: {
      label: 'Стабильно',
      description: 'Частота событий относительно предыдущего сопоставимого периода не изменилась настолько, '
        + 'чтобы считать ситуацию улучшением или ухудшением.',
    },
    worsening: {
      label: 'Ухудшается',
      description: 'Частота событий стала не менее чем на 30% выше предыдущего сопоставимого периода. '
        + 'При доступных данных учитывается фактическое время контроля камеры.',
    },
    improving: {
      label: 'Улучшается',
      description: 'Частота событий снизилась не менее чем на 30% относительно предыдущего сопоставимого периода. '
        + 'Проблема не обязательно решена.',
    },
  },
  status: {
    active: {
      label: 'Активная',
      description: 'Проблема зарегистрирована и пока не признана решённой по правилам контроля.',
    },
    improving: {
      label: 'Улучшается',
      description: 'Проблема пока остаётся активной, но динамика улучшилась; статус «Решена» не присвоен.',
    },
    resolved: {
      label: 'Решена',
      description: 'Критерий повторяемости не подтверждался в течение периода уверенного разрешения, '
        + 'а достаточность контроля камерой проверена. При новом повторении проблема может открыться снова.',
    },
    reopened: {
      label: 'Повторно открыта',
      description: 'После подтверждённого решения правило повторяемости сработало снова: начался новый эпизод.',
    },
  },
  occurrence_state: {
    new: {
      label: 'Появилась впервые',
      description: 'Начался новый эпизод проблемы для этой комбинации правила, камеры и зоны. '
        + 'Это не то же самое, что динамика «Новая».',
    },
    regular: {
      label: 'Повторяется регулярно',
      description: 'Проблема продолжает проявляться в текущем эпизоде.',
    },
    reopened: {
      label: 'Возобновилась',
      description: 'После решения проблемы зарегистрирован новый эпизод повторений.',
    },
  },
  tracking_state: {
    not_tracked: {
      label: 'Больше не отслеживается',
      description: 'Источник проблемы сейчас не отслеживается. Последний статус сохранён; '
        + 'это не подтверждение улучшения или решения.',
    },
  },
};

export const ISSUE_TERM_FALLBACK = 'Описание пока недоступно';

const reportedUnknownTerms = new Set();

/**
 * Пояснение значения поля. Незнакомый код — «Описание пока недоступно»
 * (не «Стабильно» и не «Решена»); в консоль уходят только имя поля и код.
 */
export const describeIssueTerm = (field, code) => {
  const entry = ISSUE_TERM_HELP[field]?.[code];
  if (entry) return { ...entry, context: ISSUE_FIELD_CONTEXT[field], known: true };
  const key = `${field}:${code}`;
  if (code != null && !reportedUnknownTerms.has(key)) {
    reportedUnknownTerms.add(key);
    // eslint-disable-next-line no-console
    console.warn('operational-issues: unknown enum', field, code);
  }
  return { label: null, description: ISSUE_TERM_FALLBACK, context: ISSUE_FIELD_CONTEXT[field], known: false };
};

/**
 * Какое поле объясняет итоговый бейдж состояния (deriveIssueDisplayStatus).
 * «Улучшается» из trend и из status — разные пояснения.
 */
export const displayStatusTerm = (displayId, status, trend) => {
  if (displayId === 'resolved') return { field: 'status', code: 'resolved' };
  if (displayId === 'improving') {
    return trend === 'improving' ? { field: 'trend', code: 'improving' } : { field: 'status', code: 'improving' };
  }
  if (['new', 'stable', 'worsening'].includes(displayId)) return { field: 'trend', code: displayId };
  return { field: 'status', code: status };
};
